import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type TicketDetail, type TicketSummary } from '../api.ts';
import { message, plural } from '../format.ts';
import { NewTicket } from './NewTicket.tsx';
import { ReplyForm, StatusChip, Thread } from './Thread.tsx';
import { countByStatus, formatWhen, typeLabel } from './tickets.ts';

/**
 * The account's conversations with DriftWatch.
 *
 * Every ticket anybody in the account opened is listed for everybody in it,
 * whatever their role: a viewer who reports a bug and goes on holiday should
 * not leave the reply unread by the colleague who could act on it.
 *
 * There is no email. The number of tickets with a reply waiting is the only
 * notification, so it leads the screen rather than hiding in a column.
 */

type View =
  | { readonly kind: 'list' }
  | { readonly kind: 'new' }
  | { readonly kind: 'ticket'; readonly id: string };

export const Support = () => {
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ kind: 'list' });

  const load = useCallback(() => {
    void api
      .supportTickets()
      .then((response) => {
        setTickets(response.tickets);
        setError(null);
      })
      .catch((cause: unknown) => setError(message(cause)));
  }, []);

  useEffect(load, [load]);

  if (view.kind === 'new') {
    return (
      <section className="sp-screen">
        <NewTicket
          onOpened={(ticket) => {
            load();
            setView({ kind: 'ticket', id: ticket.id });
          }}
          onCancel={() => setView({ kind: 'list' })}
        />
      </section>
    );
  }

  if (view.kind === 'ticket') {
    return (
      <section className="sp-screen">
        <TicketView
          id={view.id}
          onBack={() => {
            load();
            setView({ kind: 'list' });
          }}
        />
      </section>
    );
  }

  if (error !== null) {
    return (
      <div className="callout callout-caution" role="alert">
        {error}
      </div>
    );
  }

  if (tickets === null) {
    return (
      <p className="note" role="status">
        Loading tickets…
      </p>
    );
  }

  const waiting = countByStatus(tickets, 'awaiting_client');
  const open = () => setView({ kind: 'new' });

  if (tickets.length === 0) {
    return (
      <section className="sp-screen">
        <div className="empty">
          <span className="empty-title">No tickets yet</span>
          <p className="empty-body">
            Found something that is not working, or something you wish DriftWatch did? Open a
            ticket and the DriftWatch team will reply here. Everyone in your account can see it.
          </p>
          <div className="toolbar">
            <button type="button" className="btn btn-primary" onClick={open}>
              Open a ticket
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="sp-screen">
      <div className="toolbar sp-head">
        <p className={waiting > 0 ? 'lead sp-waiting' : 'lead'} role="status">
          {waiting > 0
            ? `${plural(waiting, 'ticket')} ${waiting === 1 ? 'has' : 'have'} a reply from support waiting for you.`
            : 'Nothing is waiting for you. Replies from support appear here — there is no email.'}
        </p>
        <span className="spacer" />
        <button type="button" className="btn btn-primary" onClick={open}>
          Open a ticket
        </button>
      </div>

      <div className="tbl-wrap">
        <table className="tbl sp-table">
          <thead>
            <tr>
              <th scope="col">Subject</th>
              <th scope="col">Type</th>
              <th scope="col">Status</th>
              <th scope="col">Opened by</th>
              <th scope="col">Last updated</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket) => (
              <tr key={ticket.id} className={ticket.status === 'closed' ? 'dimmed' : undefined}>
                <th scope="row" className="sp-subject">
                  <button
                    type="button"
                    className="link"
                    onClick={() => setView({ kind: 'ticket', id: ticket.id })}
                  >
                    {ticket.subject}
                  </button>
                </th>
                <td>{typeLabel(ticket.type)}</td>
                <td>
                  <StatusChip status={ticket.status} audience="console" />
                </td>
                <td>{ticket.openedByName}</td>
                <td className="num">{formatWhen(ticket.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

interface TicketViewProps {
  readonly id: string;
  readonly onBack: () => void;
}

const TicketView = ({ id, onBack }: TicketViewProps) => {
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .supportTicket(id)
      .then((response) => setTicket(response.ticket))
      .catch((cause: unknown) => setError(message(cause)));
  }, [id]);

  return (
    <div className="sp-stack">
      <div className="toolbar">
        <button type="button" className="btn btn-quiet btn-sm" onClick={onBack}>
          Back to all tickets
        </button>
      </div>

      {error !== null && (
        <div className="callout callout-caution" role="alert">
          {error}
        </div>
      )}
      {error === null && ticket === null && (
        <p className="note" role="status">
          Loading the ticket…
        </p>
      )}

      {ticket !== null && (
        <article className="card">
          <div className="card-head sp-ticket-head">
            <h2>{ticket.subject}</h2>
            <p className="note">
              {typeLabel(ticket.type)} · opened by {ticket.openedByName} on{' '}
              {formatWhen(ticket.createdAt)}
            </p>
            <StatusChip status={ticket.status} audience="console" />
          </div>
          <div className="card-body sp-stack">
            <Thread messages={ticket.messages} />

            {ticket.status === 'closed' ? (
              <div className="callout">
                <span className="callout-title">This ticket is closed</span>
                DriftWatch support closed it
                {ticket.closedAt === null ? '' : ` on ${formatWhen(ticket.closedAt)}`}. If
                something is still wrong, or it has come back, open a new ticket and mention this
                one.
              </div>
            ) : (
              <ReplyForm
                idPrefix="sp-reply"
                label="Your reply"
                onSend={async (reply) => {
                  try {
                    const response = await api.replyToSupportTicket(ticket.id, reply);
                    setTicket(response.ticket);
                  } catch (cause) {
                    // Closed by support while this was being written. The
                    // server's sentence stays beside the button; the thread
                    // reloads so the screen stops offering a reply box.
                    if (cause instanceof ApiError && cause.status === 409) {
                      void api
                        .supportTicket(ticket.id)
                        .then((response) => setTicket(response.ticket))
                        .catch(() => undefined);
                    }
                    throw cause;
                  }
                }}
              />
            )}
          </div>
        </article>
      )}
    </div>
  );
};
